import { ArrowRight, Mail, Star, TrendingUp, X } from 'lucide-react';
import { ButtonSpinner } from '../../../../helpers/UI/ButtonSpinner/ButtonSpinner';

export type NewsletterSignupModalProps = {
  open: boolean;
  onClose: () => void;
  onSubscribe: () => void;
  submitting: boolean;
  errorText: string | null;
};

export const NewsletterSignupModal = ({
  open,
  onClose,
  onSubscribe,
  submitting,
  errorText,
}: NewsletterSignupModalProps) => {
  if (!open) return null;

  return (
    <div
      className='fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40'
      role='presentation'
      onClick={onClose}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='newsletter-modal-title'
        className='relative w-full max-w-[420px] rounded-[20px] bg-[#F4F6FB] p-6 shadow-[0_24px_80px_rgba(15,23,42,0.18)]'
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type='button'
          onClick={onClose}
          className='absolute right-4 top-4 rounded-full p-1.5 text-[#697386] hover:bg-white/80 transition-colors'
          aria-label='Close'
        >
          <X size={20} />
        </button>

        <div className='flex flex-col items-center text-center pt-1'>
          <span
            className='inline-flex h-14 w-14 items-center justify-center rounded-[14px] bg-[#0055FF] text-white shadow-[0_12px_28px_rgba(0,85,255,0.35)]'
            aria-hidden
          >
            <Mail size={28} strokeWidth={2} />
          </span>
          <h2
            id='newsletter-modal-title'
            className='mt-5 font-display text-[26px] font-semibold text-[#0A0B14] tracking-[-0.02em]'
          >
            Stay in the loop
          </h2>
          <p className='mt-2 text-[14px] text-[#6B7285] max-w-[320px]'>
            One quick thing before you start earning rewards.
          </p>
        </div>

        <div className='mt-6 rounded-[16px] border border-[#E9EDF5] bg-white p-5 shadow-sm'>
          <div className='text-[11px] font-bold tracking-[0.12em] text-[#0055FF]'>NEWSLETTER</div>
          <div className='mt-2 font-semibold text-[18px] text-[#0A0B14]'>The Ledger Weekly</div>

          <ul className='mt-5 space-y-4 text-left'>
            <li className='flex gap-3'>
              <span
                className='mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#EFFFF1]'
                aria-hidden
              >
                <TrendingUp size={20} className='text-[#16A36E]' strokeWidth={2} />
              </span>
              <div>
                <div className='text-[14px] font-semibold text-[#0A0B14]'>Weekly hacks</div>
                <p className='mt-0.5 text-[13px] leading-[18px] text-[#6B7285]'>
                  Smart strategies to boost your credit score and save on rent.
                </p>
              </div>
            </li>
            <li className='flex gap-3'>
              <span
                className='mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#F3EEFF]'
                aria-hidden
              >
                <Star size={20} className='text-[#7C3AED]' strokeWidth={2} />
              </span>
              <div>
                <div className='text-[14px] font-semibold text-[#0A0B14]'>Early access</div>
                <p className='mt-0.5 text-[13px] leading-[18px] text-[#6B7285]'>
                  First dibs on reward drops and partner promotions.
                </p>
              </div>
            </li>
          </ul>
        </div>

        <div className='mt-6 border-t border-[#E9EDF5] pt-5'>
          {errorText && (
            <p className='mb-3 text-center text-[13px] text-red-600' role='alert'>
              {errorText}
            </p>
          )}
          <button
            type='button'
            disabled={submitting}
            onClick={onSubscribe}
            className='flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#0055FF] px-5 py-3.5 text-[15px] font-semibold text-white shadow-[0_10px_28px_rgba(0,85,255,0.35)] transition-opacity hover:opacity-95 disabled:opacity-60'
          >
            {submitting ? (
              <ButtonSpinner />
            ) : (
              <>
                Subscribe
                <ArrowRight size={20} strokeWidth={2} />
              </>
            )}
          </button>
          <p className='mt-3 text-center text-[11px] text-[#9CA3AF]'>
            Optional · unsubscribe anytime from your e-mail
          </p>
        </div>
      </div>
    </div>
  );
};
